import type {
	DepartmentRoutingSettings,
	DepartmentSummary,
	MembershipSummary,
	TenantMemberSummary,
} from '@livechat/shared';
import {
	type DragEvent,
	type FormEvent,
	type PointerEvent as ReactPointerEvent,
	useEffect,
	useRef,
	useState,
} from 'react';

import {
	createDepartment,
	deleteDepartment,
	listDepartments,
	listMembers,
	reorderDepartments,
	setDepartmentMembers,
	updateDepartment,
} from '../api.js';
import { departmentOrderChanged, moveDepartment } from '../department-order.js';
import { MemberAvatar } from './MemberAvatar.js';
import { TimezoneSelect } from './TimezoneSelect.js';

interface TenantDepartmentsProps {
	departmentId?: string;
	membership: MembershipSummary;
	onDepartmentSelected: (departmentId?: string, replace?: boolean) => void;
}

export function departmentRoutingSettings(
	data: FormData,
	offlineMode: DepartmentRoutingSettings['offlineMode'] = 'form',
): DepartmentRoutingSettings {
	return {
		assignmentMode: String(
			data.get('assignmentMode'),
		) as DepartmentRoutingSettings['assignmentMode'],
		businessHours: DAYS.map(({ day }) => ({
			day,
			enabled: data.get(`hours-${day}-enabled`) === 'on',
			end: String(data.get(`hours-${day}-end`)),
			start: String(data.get(`hours-${day}-start`)),
		})),
		offlineMode,
		timezone: String(data.get('timezone')),
	};
}

const DAYS = [
	{ day: 1, label: 'Monday' },
	{ day: 2, label: 'Tuesday' },
	{ day: 3, label: 'Wednesday' },
	{ day: 4, label: 'Thursday' },
	{ day: 5, label: 'Friday' },
	{ day: 6, label: 'Saturday' },
	{ day: 0, label: 'Sunday' },
] as const;

const DEFAULT_START_MINUTES = 9 * 60;
const DEFAULT_END_MINUTES = 17 * 60 + 30;
const LAST_DAY_MINUTE = 24 * 60 - 1;
const MINIMUM_OPENING_MINUTES = 15;

export function constrainOperationalHours(
	boundary: 'end' | 'start',
	value: number,
	current: { end: number; start: number },
): { end: number; start: number } {
	const bounded = Math.min(LAST_DAY_MINUTE, Math.max(0, value));
	return boundary === 'start'
		? {
				...current,
				start: Math.min(bounded, Math.max(0, current.end - MINIMUM_OPENING_MINUTES)),
			}
		: {
				...current,
				end: Math.max(
					bounded,
					Math.min(LAST_DAY_MINUTE, current.start + MINIMUM_OPENING_MINUTES),
				),
			};
}

function timeToMinutes(value: string, fallback: number): number {
	const [hours, minutes] = value.split(':').map(Number);
	if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
	return Math.min(1439, Math.max(0, hours! * 60 + minutes!));
}

function minutesToTime(value: number): string {
	const bounded = Math.min(1439, Math.max(0, Math.round(value)));
	return `${String(Math.floor(bounded / 60)).padStart(2, '0')}:${String(bounded % 60).padStart(2, '0')}`;
}

function initialBusinessHours(settings?: DepartmentRoutingSettings) {
	return DAYS.map(({ day, label }) => {
		const configured = settings?.businessHours.find((interval) => interval.day === day);
		return {
			day,
			enabled: configured?.enabled ?? (day >= 1 && day <= 5),
			end: timeToMinutes(configured?.end ?? '', DEFAULT_END_MINUTES),
			label,
			start: timeToMinutes(configured?.start ?? '', DEFAULT_START_MINUTES),
		};
	});
}

function OperationalHoursEditor({
	disabled,
	settings,
}: {
	disabled: boolean;
	settings?: DepartmentRoutingSettings;
}) {
	const [hours, setHours] = useState(() => initialBusinessHours(settings));
	const activeRange = useRef<
		| {
				boundary: 'end' | 'start';
				day: number;
				pointerId: number;
		  }
		| undefined
	>(undefined);

	function updateDay(day: number, update: Partial<(typeof hours)[number]>): void {
		setHours((current) =>
			current.map((interval) =>
				interval.day === day ? { ...interval, ...update } : interval,
			),
		);
	}

	function pointerMinutes(event: ReactPointerEvent<HTMLDivElement>): number {
		const bounds = event.currentTarget.getBoundingClientRect();
		const percentage = (event.clientX - bounds.left) / bounds.width;
		return Math.round((percentage * LAST_DAY_MINUTE) / 15) * 15;
	}

	function updateRange(day: number, boundary: 'end' | 'start', value: number): void {
		setHours((current) =>
			current.map((interval) =>
				interval.day === day
					? {
							...interval,
							...constrainOperationalHours(boundary, value, interval),
						}
					: interval,
			),
		);
	}

	function beginRangeDrag(
		interval: (typeof hours)[number],
		event: ReactPointerEvent<HTMLDivElement>,
	): void {
		if (disabled || !interval.enabled || event.button !== 0) return;
		const value = pointerMinutes(event);
		const boundary =
			Math.abs(value - interval.start) <= Math.abs(value - interval.end) ? 'start' : 'end';
		activeRange.current = { boundary, day: interval.day, pointerId: event.pointerId };
		event.currentTarget.setPointerCapture(event.pointerId);
		updateRange(interval.day, boundary, value);
		event.preventDefault();
	}

	function continueRangeDrag(day: number, event: ReactPointerEvent<HTMLDivElement>): void {
		const active = activeRange.current;
		if (!active || active.day !== day || active.pointerId !== event.pointerId) return;
		updateRange(day, active.boundary, pointerMinutes(event));
	}

	function endRangeDrag(event: ReactPointerEvent<HTMLDivElement>): void {
		if (activeRange.current?.pointerId !== event.pointerId) return;
		activeRange.current = undefined;
		if (event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}
	}

	return (
		<section className="settings-card department-panel operational-hours-panel">
			<div className="integration-panel-heading">
				<h4 className="panel-title">Operational Hours</h4>
				<p>Set the local opening window for each day.</p>
			</div>
			<label className="operational-timezone">
				<span>Timezone</span>
				<TimezoneSelect
					name="timezone"
					defaultValue={settings?.timezone ?? 'Europe/London'}
					disabled={disabled}
				/>
			</label>
			<div className="operational-hours-list">
				{hours.map((interval) => {
					const startPercent = (interval.start / LAST_DAY_MINUTE) * 100;
					const endPercent = (interval.end / LAST_DAY_MINUTE) * 100;
					return (
						<div
							className={`operational-hours-row${interval.enabled ? '' : ' closed'}`}
							key={interval.day}
						>
							<label className="operational-day-toggle">
								<input
									checked={interval.enabled}
									disabled={disabled}
									name={`hours-${interval.day}-enabled`}
									onChange={(event) =>
										updateDay(interval.day, {
											enabled: event.currentTarget.checked,
										})
									}
									type="checkbox"
								/>
								<span>{interval.label}</span>
							</label>
							<input
								name={`hours-${interval.day}-start`}
								type="hidden"
								value={minutesToTime(interval.start)}
							/>
							<input
								name={`hours-${interval.day}-end`}
								type="hidden"
								value={minutesToTime(interval.end)}
							/>
							<div
								className="operational-hours-range"
								onPointerCancel={endRangeDrag}
								onPointerDown={(event) => beginRangeDrag(interval, event)}
								onPointerMove={(event) => continueRangeDrag(interval.day, event)}
								onPointerUp={endRangeDrag}
							>
								<span
									aria-hidden="true"
									className="operational-hours-track"
									style={{
										background: `linear-gradient(to right, var(--border) 0 ${startPercent}%, var(--primary) ${startPercent}% ${endPercent}%, var(--border) ${endPercent}% 100%)`,
									}}
								/>
								<input
									aria-label={`${interval.label} opening time`}
									className="operational-hours-range-start"
									disabled={disabled || !interval.enabled}
									max={LAST_DAY_MINUTE}
									min={0}
									onChange={(event) =>
										updateDay(
											interval.day,
											constrainOperationalHours(
												'start',
												Number(event.currentTarget.value),
												interval,
											),
										)
									}
									step={15}
									type="range"
									value={interval.start}
								/>
								<input
									aria-label={`${interval.label} closing time`}
									className="operational-hours-range-end"
									disabled={disabled || !interval.enabled}
									max={LAST_DAY_MINUTE}
									min={0}
									onChange={(event) =>
										updateDay(
											interval.day,
											constrainOperationalHours(
												'end',
												Number(event.currentTarget.value),
												interval,
											),
										)
									}
									step={15}
									type="range"
									value={interval.end}
								/>
							</div>
							<output className="operational-hours-output">
								{interval.enabled
									? `${minutesToTime(interval.start)} – ${minutesToTime(interval.end)}`
									: 'Closed'}
							</output>
						</div>
					);
				})}
			</div>
		</section>
	);
}

function DepartmentTeamPicker({
	disabled,
	members,
	selectedUserIds = [],
}: {
	disabled: boolean;
	members: TenantMemberSummary[];
	selectedUserIds?: string[];
}) {
	const [query, setQuery] = useState('');
	const availableMembers = members.filter((member) => member.status !== 'suspended');
	const [selected, setSelected] = useState(
		() =>
			new Set(
				selectedUserIds.filter((userId) =>
					availableMembers.some((member) => member.id === userId),
				),
			),
	);
	const normalisedQuery = query.trim().toLocaleLowerCase();
	const visibleMembers = availableMembers.filter((member) =>
		`${member.displayName} ${member.email}`.toLocaleLowerCase().includes(normalisedQuery),
	);

	function toggleMember(userId: string, checked: boolean): void {
		setSelected((current) => {
			const next = new Set(current);
			if (checked) next.add(userId);
			else next.delete(userId);
			return next;
		});
	}

	return (
		<section className="settings-card department-panel department-team-panel">
			<div className="integration-panel-heading">
				<h4 className="panel-title">Team</h4>
			</div>
			<label className="department-team-search">
				<span className="visually-hidden">Find team members</span>
				<input
					disabled={disabled}
					onChange={(event) => setQuery(event.currentTarget.value)}
					placeholder="Find team members…"
					type="search"
					value={query}
				/>
			</label>
			{[...selected].map((userId) => (
				<input key={userId} name="memberId" type="hidden" value={userId} />
			))}
			<div
				className="department-team-picker"
				role="group"
				aria-label="Department team members"
			>
				{visibleMembers.map((member) => {
					const secondary =
						member.status === 'invited' ? 'Invitation pending' : member.email.trim();
					return (
						<label className="department-team-member" key={member.id}>
							<input
								checked={selected.has(member.id)}
								disabled={disabled}
								onChange={(event) =>
									toggleMember(member.id, event.currentTarget.checked)
								}
								type="checkbox"
							/>
							<MemberAvatar
								dataUrl={
									member.avatarKind === 'uploaded'
										? member.avatarDataUrl
										: undefined
								}
								displayName={member.displayName}
								enabled={member.avatarEnabled}
								preset={member.avatarPreset}
								size="small"
							/>
							<span className="department-team-member-copy">
								<strong>{member.displayName}</strong>
								{secondary && <span>{secondary}</span>}
							</span>
							<span aria-hidden="true" className="department-team-check">
								✓
							</span>
						</label>
					);
				})}
				{visibleMembers.length === 0 && (
					<p className="department-team-empty">No team members match your search.</p>
				)}
			</div>
		</section>
	);
}

export function departmentPageHeading(name: string): string {
	return /\bdepartment$/i.test(name) ? name : `${name} Department`;
}

function DepartmentMembers({ department }: { department: DepartmentSummary }) {
	const visibleMembers = department.members.slice(0, 8);
	const remainingMembers = department.members.slice(8);
	return (
		<span
			aria-label={
				department.members.length
					? `Members: ${department.members.map((member) => member.displayName).join(', ')}`
					: 'No team members assigned'
			}
			className="department-member-stack"
			role="group"
		>
			{visibleMembers.map((member) =>
				member.avatarEnabled ? (
					<MemberAvatar
						dataUrl={
							member.avatarKind === 'uploaded' ? member.avatarDataUrl : undefined
						}
						displayName={member.displayName}
						enabled
						key={member.userId}
						preset={member.avatarPreset}
						size="small"
					/>
				) : (
					<span
						aria-label={member.displayName}
						className="department-member-placeholder"
						key={member.userId}
						title={member.displayName}
					>
						{member.displayName.charAt(0).toUpperCase()}
					</span>
				),
			)}
			{remainingMembers.length > 0 && (
				<span
					className="department-member-overflow"
					title={remainingMembers.map((member) => member.displayName).join(', ')}
				>
					+{remainingMembers.length}
				</span>
			)}
			{department.members.length === 0 && (
				<span className="department-no-members">No members</span>
			)}
		</span>
	);
}

function DepartmentConfiguration({
	canManage,
	department,
	members,
	onDeleted,
	onDepartmentSelected,
	onUpdated,
	tenantId,
}: {
	canManage: boolean;
	department: DepartmentSummary;
	members: TenantMemberSummary[];
	onDeleted: (departmentId: string) => void;
	onDepartmentSelected: (departmentId?: string, replace?: boolean) => void;
	onUpdated: (department: DepartmentSummary) => void;
	tenantId: string;
}) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!canManage) return;
		setBusy(true);
		setError(undefined);
		const data = new FormData(event.currentTarget);
		try {
			await updateDepartment(tenantId, department.id, {
				name: String(data.get('name')),
				routingSettings: departmentRoutingSettings(
					data,
					department.routingSettings.offlineMode,
				),
				status: String(data.get('status')) as DepartmentSummary['status'],
			});
			const assigned = data.getAll('memberId').map((userId) => ({
				priority: 100,
				userId: String(userId),
			}));
			onUpdated(await setDepartmentMembers(tenantId, department.id, assigned));
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not save the department');
		} finally {
			setBusy(false);
		}
	}

	async function remove(): Promise<void> {
		if (
			!window.confirm(
				`Delete ${department.name}? Closed chats will remain in history, but their department association will be removed.`,
			)
		) {
			return;
		}
		setBusy(true);
		setError(undefined);
		try {
			await deleteDepartment(tenantId, department.id);
			onDeleted(department.id);
			onDepartmentSelected(undefined, true);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not delete the department');
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="department-detail-page">
			<div className="subsection-heading department-detail-heading">
				<div>
					<h3>{departmentPageHeading(department.name)}</h3>
					<p>Configure chat assignment, operational hours and the team.</p>
				</div>
				<span className={`status-pill ${department.status}`}>{department.status}</span>
			</div>
			{error && <div className="alert error">{error}</div>}
			<form
				className="department-detail-form department-settings-form"
				onSubmit={(event) => void save(event)}
			>
				<section className="settings-card department-panel department-general-panel">
					<div className="integration-panel-heading">
						<h4 className="panel-title">General</h4>
						<p>Set the department name, availability and assignment method.</p>
					</div>
					<div className="department-general-fields">
						<label>
							<span>Name</span>
							<input
								name="name"
								defaultValue={department.name}
								disabled={!canManage || busy}
								required
							/>
						</label>
						<label>
							<span>Assignment</span>
							<select
								name="assignmentMode"
								defaultValue={department.routingSettings.assignmentMode}
								disabled={!canManage || busy}
							>
								<option value="round_robin">Round robin</option>
								<option value="least_active">Least active chats</option>
								<option value="manual">Manual pickup</option>
							</select>
						</label>
						<label>
							<span>Department status</span>
							<select
								name="status"
								defaultValue={department.status}
								disabled={!canManage || busy}
							>
								<option value="active">Active</option>
								<option value="disabled">Disabled</option>
							</select>
						</label>
					</div>
				</section>
				<OperationalHoursEditor
					disabled={!canManage || busy}
					settings={department.routingSettings}
				/>
				<DepartmentTeamPicker
					disabled={!canManage || busy}
					members={members}
					selectedUserIds={department.members.map((member) => member.userId)}
				/>
				{canManage ? (
					<div className="department-form-actions">
						<button
							className="text-button danger"
							disabled={busy}
							onClick={() => void remove()}
							type="button"
						>
							Delete department
						</button>
						<div>
							<button className="primary-button" disabled={busy}>
								{busy ? 'Saving…' : 'Save department'}
							</button>
						</div>
					</div>
				) : (
					<p className="settings-readonly-note">
						Only owners and administrators can change department settings.
					</p>
				)}
			</form>
		</div>
	);
}

export function TenantDepartments({
	departmentId,
	membership,
	onDepartmentSelected,
}: TenantDepartmentsProps) {
	const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
	const [members, setMembers] = useState<TenantMemberSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [showCreate, setShowCreate] = useState(false);
	const [busy, setBusy] = useState(false);
	const [reordering, setReordering] = useState(false);
	const [draggedDepartmentId, setDraggedDepartmentId] = useState<string>();
	const [error, setError] = useState<string>();
	const departmentsRef = useRef<DepartmentSummary[]>([]);
	const dragStartOrderRef = useRef<DepartmentSummary[]>([]);
	const draggedDepartmentIdRef = useRef<string | undefined>(undefined);
	const dragTargetDepartmentIdRef = useRef<string | undefined>(undefined);
	const canManage = membership.role === 'owner' || membership.role === 'admin';

	useEffect(() => {
		setLoading(true);
		void Promise.all([
			listDepartments(membership.tenantId),
			canManage ? listMembers(membership.tenantId) : Promise.resolve([]),
		])
			.then(([departmentResult, memberResult]) => {
				departmentsRef.current = departmentResult;
				setDepartments(departmentResult);
				setMembers(memberResult);
			})
			.catch((caught: unknown) =>
				setError(caught instanceof Error ? caught.message : 'Could not load departments'),
			)
			.finally(() => setLoading(false));
	}, [canManage, membership.tenantId]);

	function updateDepartments(
		updater: (current: DepartmentSummary[]) => DepartmentSummary[],
	): void {
		setDepartments((current) => {
			const updated = updater(current);
			departmentsRef.current = updated;
			return updated;
		});
	}

	function startDepartmentDrag(event: DragEvent<HTMLSpanElement>, departmentId: string): void {
		if (!canManage || reordering) {
			event.preventDefault();
			return;
		}
		dragStartOrderRef.current = departmentsRef.current;
		draggedDepartmentIdRef.current = departmentId;
		dragTargetDepartmentIdRef.current = departmentId;
		setDraggedDepartmentId(departmentId);
		event.dataTransfer.effectAllowed = 'move';
		event.dataTransfer.setData('text/plain', departmentId);
	}

	function moveDraggedDepartment(
		event: DragEvent<HTMLElement>,
		targetDepartmentId: string,
	): void {
		const draggedId = draggedDepartmentIdRef.current;
		if (
			!draggedId ||
			draggedId === targetDepartmentId ||
			dragTargetDepartmentIdRef.current === targetDepartmentId
		) {
			return;
		}
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
		dragTargetDepartmentIdRef.current = targetDepartmentId;
		updateDepartments((current) => moveDepartment(current, draggedId, targetDepartmentId));
	}

	async function finishDepartmentDrag(): Promise<void> {
		const before = dragStartOrderRef.current;
		const reordered = departmentsRef.current;
		draggedDepartmentIdRef.current = undefined;
		dragTargetDepartmentIdRef.current = undefined;
		setDraggedDepartmentId(undefined);
		if (!departmentOrderChanged(before, reordered)) return;

		setReordering(true);
		setError(undefined);
		try {
			const saved = await reorderDepartments(
				membership.tenantId,
				reordered.map((department) => department.id),
			);
			departmentsRef.current = saved;
			setDepartments(saved);
		} catch (caught) {
			departmentsRef.current = before;
			setDepartments(before);
			setError(caught instanceof Error ? caught.message : 'Could not reorder departments');
		} finally {
			setReordering(false);
		}
	}

	async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		setBusy(true);
		setError(undefined);
		const form = event.currentTarget;
		const data = new FormData(form);
		try {
			let department = await createDepartment(membership.tenantId, {
				name: String(data.get('name')),
				routingSettings: departmentRoutingSettings(data),
			});
			const assignedMembers = data.getAll('memberId').map((userId) => ({
				priority: 100,
				userId: String(userId),
			}));
			if (assignedMembers.length) {
				department = await setDepartmentMembers(
					membership.tenantId,
					department.id,
					assignedMembers,
				);
			}
			updateDepartments((current) => [...current, department]);
			setShowCreate(false);
			form.reset();
			onDepartmentSelected(department.id);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not create department');
		} finally {
			setBusy(false);
		}
	}

	if (loading) return <p>Loading departments…</p>;

	if (departmentId) {
		const department = departments.find((item) => item.id === departmentId);
		if (!department) {
			return <div className="alert error">This department could not be found.</div>;
		}
		return (
			<DepartmentConfiguration
				canManage={canManage}
				department={department}
				key={department.id}
				members={members}
				onDeleted={(deletedId) =>
					updateDepartments((current) => current.filter((item) => item.id !== deletedId))
				}
				onDepartmentSelected={onDepartmentSelected}
				onUpdated={(updated) =>
					updateDepartments((current) =>
						current.map((item) => (item.id === updated.id ? updated : item)),
					)
				}
				tenantId={membership.tenantId}
			/>
		);
	}

	return (
		<div>
			<div className="subsection-heading">
				<div>
					<h3>Departments</h3>
					<p>
						Organise agents into teams and control how each team's chats are assigned.
					</p>
				</div>
				{canManage && (
					<button
						className="primary-button"
						onClick={() => setShowCreate((value) => !value)}
					>
						{showCreate ? 'Cancel' : 'Add department'}
					</button>
				)}
			</div>
			{error && <div className="alert error">{error}</div>}
			{showCreate && (
				<form
					className="department-create-form department-settings-form"
					onSubmit={(event) => void submit(event)}
				>
					<section className="settings-card department-panel department-general-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">General</h4>
							<p>Set the department name and assignment method.</p>
						</div>
						<div className="department-general-fields">
							<label>
								<span>Name</span>
								<input name="name" required placeholder="Customer service" />
							</label>
							<label>
								<span>Assignment</span>
								<select name="assignmentMode" defaultValue="round_robin">
									<option value="round_robin">Round robin</option>
									<option value="least_active">Least active chats</option>
									<option value="manual">Manual pickup</option>
								</select>
							</label>
						</div>
					</section>
					<OperationalHoursEditor disabled={busy} />
					<DepartmentTeamPicker disabled={busy} members={members} />
					<div className="department-form-actions department-create-actions">
						<button className="primary-button" disabled={busy}>
							{busy ? 'Creating…' : 'Create department'}
						</button>
					</div>
				</form>
			)}
			<div className="department-list">
				{departments.map((department) => (
					<article
						className={`department-list-item${draggedDepartmentId === department.id ? ' dragging' : ''}`}
						key={department.id}
						onDragEnter={(event) => moveDraggedDepartment(event, department.id)}
						onDragOver={(event) => {
							if (draggedDepartmentIdRef.current) event.preventDefault();
						}}
						onDrop={(event) => event.preventDefault()}
					>
						<button
							className="department-list-row"
							disabled={!canManage}
							onClick={() => onDepartmentSelected(department.id)}
							type="button"
						>
							<span
								aria-hidden="true"
								className={`department-drag-handle${canManage ? '' : ' disabled'}`}
								draggable={canManage && !reordering}
								onClick={(event) => event.stopPropagation()}
								onDragEnd={() => void finishDepartmentDrag()}
								onDragStart={(event) => startDepartmentDrag(event, department.id)}
							>
								⋮⋮
							</span>
							<span className="department-list-identity">
								<strong>{department.name}</strong>
								<span>
									{department.routingSettings.assignmentMode.replace('_', ' ')} ·{' '}
									{department.routingSettings.timezone}
								</span>
							</span>
							<DepartmentMembers department={department} />
							<span className={`status-pill ${department.status}`}>
								{department.status}
							</span>
						</button>
					</article>
				))}
				{departments.length === 0 && (
					<div className="empty-configuration">No departments configured yet.</div>
				)}
			</div>
		</div>
	);
}
